import { useEffect, useState, useRef } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import {
  Building2,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  MapPin,
  Users as UsersIcon,
  Copy,
  LayoutGrid,
  List,
  ExternalLink,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type Room, type OrgSettings, type Building } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { PageTabs } from '../components/PageTabs'

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  JPY: '¥',
  CHF: 'CHF',
  SGD: 'S$',
  HKD: 'HK$',
}

export function RoomsPage() {
  const navigate = useNavigate()
  const { user, currentOrg, isAuthenticated, isLoading: authLoading, checkAuth } = useAuthStore()

  const [rooms, setRooms] = useState<Room[]>([])
  const [buildings, setBuildings] = useState<Building[]>([])
  const [orgSettings, setOrgSettings] = useState<OrgSettings>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    // Default to list view if more than 6 rooms (will be set after load)
    const saved = localStorage.getItem('roomsi-rooms-view')
    return saved === 'list' || saved === 'cards' ? saved : 'cards'
  })

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [authLoading, isAuthenticated, navigate])

  // Manage-only page: guests browse and book rooms through the marketplace
  useEffect(() => {
    if (
      !authLoading &&
      isAuthenticated &&
      currentOrg &&
      currentOrg.role !== 'admin' &&
      currentOrg.role !== 'approver'
    ) {
      navigate({ to: '/my-bookings' })
    }
  }, [authLoading, isAuthenticated, currentOrg, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      loadRooms()
    }
  }, [isAuthenticated])

  const loadRooms = async () => {
    try {
      setIsLoading(true)
      const [roomsData, settingsData, buildingsData] = await Promise.all([
        api.rooms.list(),
        api.settings.get(),
        api.buildings.list(),
      ])
      // Sort rooms alphabetically by name
      const sortedRooms = [...roomsData.rooms].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      )
      setRooms(sortedRooms)
      setOrgSettings(settingsData.organisation.settings || {})
      setBuildings(buildingsData.buildings)
    } catch (err: any) {
      setError(err.message || 'Failed to load rooms')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (roomId: string, roomName: string) => {
    const confirmed = confirm(
      `⚠️ PERMANENTLY DELETE "${roomName}"?\n\n` +
        `This will delete:\n` +
        `• The room and all its settings\n` +
        `• All bookings for this room\n` +
        `• All images\n\n` +
        `This action CANNOT be undone.`
    )
    if (!confirmed) return

    try {
      await api.rooms.delete(roomId)
      setRooms(rooms.filter((r) => r.id !== roomId))
    } catch (err: any) {
      setError(err.message || 'Failed to delete room')
    }
  }

  const handleDuplicate = async (roomId: string) => {
    try {
      const result = await api.rooms.duplicate(roomId)
      // Add and re-sort alphabetically
      const newRooms = [...rooms, result.room].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      )
      setRooms(newRooms)
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate room')
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return null
  }

  const isAdmin = currentOrg?.role === 'admin'

  // Create a map of building ID to building for quick lookup
  const buildingsMap = new Map<string, Building>(buildings.map((b) => [b.id, b]))

  const headerActions = (
    <>
      {/* View toggle */}
      <div className="flex items-center bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => {
            setViewMode('cards')
            localStorage.setItem('roomsi-rooms-view', 'cards')
          }}
          className={`p-1.5 rounded ${viewMode === 'cards' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          title="Card view"
        >
          <LayoutGrid className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            setViewMode('list')
            localStorage.setItem('roomsi-rooms-view', 'list')
          }}
          className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          title="List view"
        >
          <List className="w-4 h-4" />
        </button>
      </div>
      {isAdmin && (
        <Link
          to="/dashboard/rooms/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Room
        </Link>
      )}
    </>
  )

  const isManager = currentOrg?.role === 'admin' || currentOrg?.role === 'approver'

  return (
    <DashboardLayout title="Rooms" actions={headerActions}>
      {isManager && (
        <PageTabs
          tabs={[
            { label: 'Rooms', to: '/dashboard/rooms' },
            { label: 'Buildings', to: '/dashboard/buildings' },
          ]}
          active="/dashboard/rooms"
        />
      )}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : rooms.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {currentOrg?.role === 'guest' ? 'No rooms available' : 'No rooms yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {currentOrg?.role === 'guest'
              ? 'There are no rooms available for booking at this time.'
              : 'Get started by creating your first room'}
          </p>
          {isAdmin && (
            <Link
              to="/dashboard/rooms/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Room
            </Link>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          {rooms.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              building={room.buildingId ? buildingsMap.get(room.buildingId) : undefined}
              isAdmin={isAdmin}
              currency={orgSettings.currency || 'USD'}
              onDelete={() => handleDelete(room.id, room.name)}
              onDuplicate={() => handleDuplicate(room.id)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 sm:px-4 py-2 sm:py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Room
                </th>
                <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Location
                </th>
                <th className="text-center sm:text-left px-2 sm:px-4 py-2 sm:py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Capacity
                </th>
                <th className="hidden lg:table-cell text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Rate
                </th>
                <th className="text-right px-2 sm:px-4 py-2 sm:py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rooms.map((room) => (
                <RoomRow
                  key={room.id}
                  room={room}
                  building={room.buildingId ? buildingsMap.get(room.buildingId) : undefined}
                  isAdmin={isAdmin}
                  currency={orgSettings.currency || 'USD'}
                  onDelete={() => handleDelete(room.id, room.name)}
                  onDuplicate={() => handleDuplicate(room.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  )
}

function RoomCard({
  room,
  building,
  isAdmin,
  currency,
  onDelete,
  onDuplicate,
}: {
  room: Room
  building?: Building
  isAdmin: boolean
  currency: string
  onDelete: () => void
  onDuplicate: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const currencySymbol = CURRENCY_SYMBOLS[currency] || '$'
  const navigate = useNavigate()

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on menu or its children
    if ((e.target as HTMLElement).closest('[data-menu]')) return
    navigate({ to: `/dashboard/rooms/${room.id}/edit` })
  }

  // Get location display: prefer building address, fall back to room location
  const displayLocation = building?.address || room.location
  const displayMapsUrl = building?.googleMapsUrl

  return (
    <div
      className="bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Image placeholder */}
      <div className="aspect-[4/3] sm:h-52 bg-gray-100 flex items-center justify-center overflow-hidden rounded-t-lg sm:rounded-t-xl">
        {room.images && room.images.length > 0 ? (
          <img
            src={room.images[room.heroImageIndex ?? 0]?.url || room.images[0].url}
            alt={room.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-primary-300" />
        )}
      </div>

      <div className="p-2.5 sm:p-4">
        <div className="flex items-start justify-between mb-1 sm:mb-2">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">
              {room.name}
            </h3>
            {room.isPublic && (
              <span className="hidden sm:inline px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                Public
              </span>
            )}
          </div>
          {isAdmin && (
            <div className="relative flex-shrink-0" data-menu>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setShowMenu(!showMenu)
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[120px]">
                    <Link
                      to={`/dashboard/rooms/${room.id}/edit`}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </Link>
                    <button
                      onClick={() => {
                        setShowMenu(false)
                        onDuplicate()
                      }}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                    >
                      <Copy className="w-4 h-4" />
                      Duplicate
                    </button>
                    <button
                      onClick={() => {
                        setShowMenu(false)
                        onDelete()
                      }}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Building name badge - hidden on mobile */}
        {building && (
          <div className="hidden sm:block mb-2">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
              <Building2 className="w-3 h-3" />
              {building.name}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-1.5 sm:mb-3">
          <span className="flex items-center gap-1">
            <UsersIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            {room.capacity}
          </span>
          {displayLocation && (
            <span className="hidden sm:flex items-center gap-1">
              {displayMapsUrl ? (
                <a
                  href={displayMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
                >
                  <MapPin className="w-4 h-4" />
                  <span className="underline truncate max-w-[150px]">{displayLocation}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              ) : (
                <LocationDisplay location={displayLocation} />
              )}
            </span>
          )}
        </div>

        {room.hourlyRate > 0 && (
          <div className="text-xs sm:text-sm font-medium text-primary-600">
            {currencySymbol}
            {room.hourlyRate}/hr
          </div>
        )}

        {room.amenities && room.amenities.length > 0 && (
          <div className="hidden sm:flex flex-wrap gap-1 mt-3">
            {room.amenities.slice(0, 3).map((amenity) => (
              <span key={amenity} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                {amenity}
              </span>
            ))}
            {room.amenities.length > 3 && (
              <span className="px-2 py-0.5 text-gray-500 text-xs">
                +{room.amenities.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Table row menu with viewport-aware positioning
function TableRowMenu({
  room,
  onDuplicate,
  onDelete,
}: {
  room: Room
  onDuplicate: () => void
  onDelete: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleToggle = () => {
    if (!showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const menuHeight = 130 // approximate menu height
      const viewportHeight = window.innerHeight

      // Check if menu would go off the bottom
      const spaceBelow = viewportHeight - rect.bottom
      const openUpward = spaceBelow < menuHeight

      setMenuPosition({
        top: openUpward ? rect.top - menuHeight : rect.bottom + 4,
        left: rect.right - 120, // menu width is ~120px
      })
    }
    setShowMenu(!showMenu)
  }

  return (
    <div className="inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {showMenu && menuPosition && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setShowMenu(false)} />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[101] min-w-[120px]"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <Link
              to={`/dashboard/rooms/${room.id}/edit`}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </Link>
            <button
              onClick={() => {
                setShowMenu(false)
                onDuplicate()
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>
            <button
              onClick={() => {
                setShowMenu(false)
                onDelete()
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function RoomRow({
  room,
  building,
  isAdmin,
  currency,
  onDelete,
  onDuplicate,
}: {
  room: Room
  building?: Building
  isAdmin: boolean
  currency: string
  onDelete: () => void
  onDuplicate: () => void
}) {
  const currencySymbol = CURRENCY_SYMBOLS[currency] || '$'
  const navigate = useNavigate()

  // Get location display: prefer building address, fall back to room location
  const displayLocation = building?.address || room.location
  const displayMapsUrl = building?.googleMapsUrl

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => navigate({ to: `/dashboard/rooms/${room.id}/edit` })}
    >
      <td className="px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-10 h-10 sm:w-10 sm:h-10 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {room.images && room.images.length > 0 ? (
              <img
                src={room.images[room.heroImageIndex ?? 0]?.url || room.images[0].url}
                alt={room.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Building2 className="w-5 h-5 text-primary-300" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 text-sm sm:text-base truncate">
                {room.name}
              </span>
              {room.isPublic && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full flex-shrink-0">
                  Public
                </span>
              )}
            </div>
            {building && (
              <div className="text-xs text-primary-600 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {building.name}
              </div>
            )}
            {!building && room.amenities && room.amenities.length > 0 && (
              <div className="text-xs text-gray-500 truncate">
                {room.amenities.slice(0, 2).join(', ')}
                {room.amenities.length > 2 && ` +${room.amenities.length - 2}`}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-600">
        {building ? (
          <div>
            <div className="font-medium text-gray-900 flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5 text-primary-500" />
              {building.name}
            </div>
            {building.address && (
              <div className="text-xs text-gray-500 mt-0.5">{building.address}</div>
            )}
          </div>
        ) : displayLocation ? (
          displayMapsUrl ? (
            <a
              href={displayMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
            >
              <span className="underline truncate max-w-[200px]">{displayLocation}</span>
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </a>
          ) : (
            displayLocation
          )
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-600 text-center sm:text-left">
        {room.capacity}
      </td>
      <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-600">
        {room.hourlyRate > 0 ? `${currencySymbol}${room.hourlyRate}/hr` : '-'}
      </td>
      <td className="px-2 sm:px-4 py-2 sm:py-3 text-right">
        {isAdmin && <TableRowMenu room={room} onDuplicate={onDuplicate} onDelete={onDelete} />}
      </td>
    </tr>
  )
}
// Helper to check if a string is a Google Maps URL
function isGoogleMapsUrl(str: string): boolean {
  return (
    str.includes('google.com/maps') ||
    str.includes('goo.gl/maps') ||
    str.includes('maps.app.goo.gl')
  )
}

// Component to display location - renders as link if it's a Google Maps URL
function LocationDisplay({ location }: { location: string }) {
  const isMapLink = isGoogleMapsUrl(location)

  if (isMapLink) {
    return (
      <a
        href={location}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
      >
        <MapPin className="w-4 h-4" />
        <span className="underline">View on Maps</span>
        <ExternalLink className="w-3 h-3" />
      </a>
    )
  }

  return (
    <span className="flex items-center gap-1">
      <MapPin className="w-4 h-4" />
      {location}
    </span>
  )
}
